import { Heart, Info, Play, ShoppingCart } from "lucide-react";
import { Link } from "react-router-dom";

import { CelebrationIcon } from "@/components/icons/CelebrationIcon";
import { formatDuration } from "@/lib/helpers";
import type { CourseWithDetails } from "@/lib/schema";
import { cn } from "@/lib/utils";

interface LibraryTutorialCardProps {
  course: CourseWithDetails;
  instructorImage: string;
  isLoggedIn: boolean;
  isOwned: boolean;
  isSaved: boolean;
  inCart: boolean;
  addingFree: boolean;
  loginHref: string;
  onAddFree: (courseId: string) => void;
  onAddToCart: (course: CourseWithDetails) => void;
  onOpenPreview: () => void;
  onToggleSaved: () => void;
}

export function LibraryTutorialCard({
  course,
  instructorImage,
  isLoggedIn,
  isOwned,
  isSaved,
  inCart,
  addingFree,
  loginHref,
  onAddFree,
  onAddToCart,
  onOpenPreview,
  onToggleSaved,
}: LibraryTutorialCardProps) {
  const duration = course.total_duration ? formatDuration(course.total_duration) : null;
  const primaryLabel = getPrimaryLabel(course, isLoggedIn, isOwned, inCart, addingFree);

  return (
    <article className="relative flex h-full flex-col rounded-[1.5rem] bg-white p-[2rem_1.5rem_1.5rem] shadow-[0_24px_55px_rgba(74,25,60,0.07)] max-[479px]:rounded-[1.25rem] max-[479px]:p-[2rem_1rem_1rem]">
      {course.is_free ? (
        <div className="absolute -left-4 top-0 z-[1] -translate-y-1/2 flex items-center gap-1 rounded-full bg-white px-[0.88rem] py-2 text-[0.88rem] font-semibold text-[#3aa51d] whitespace-nowrap shadow-[0_0_0_6px_var(--background-color--background-primary,#fafafa),inset_0_0_0_1px_rgba(255,167,36,0.1)]">
          <CelebrationIcon className="h-4 w-4 shrink-0" />
          <div>FREE</div>
        </div>
      ) : course.most_popular ? (
        <div className="absolute -left-4 top-0 z-[1] -translate-y-1/2 flex items-center gap-1 rounded-full bg-white px-[0.88rem] py-2 text-[0.88rem] font-semibold text-[#762a5c] whitespace-nowrap shadow-[0_0_0_6px_var(--background-color--background-primary,#fafafa),inset_0_0_0_1px_rgba(255,167,36,0.1)]">
          <CelebrationIcon className="h-4 w-4 shrink-0" />
          <div>Most Popular</div>
        </div>
      ) : null}

      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 items-center gap-[0.8rem]">
          <div className="h-9 w-9 shrink-0 overflow-hidden rounded-full">
            <img
              src={instructorImage}
              alt={course.Instructor?.name ?? "Instructor"}
              className="h-full w-full object-cover"
              loading="lazy"
            />
          </div>
          <div className="min-w-0">
            <div className="text-[0.65rem] font-bold uppercase tracking-[0.06em] text-[#b0a4aa]">Instructor</div>
            <div className="text-[1rem] font-medium leading-[1.5] text-[#2f2830]">
              {course.Instructor?.name ?? "Khusbu Gemmati"}
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            className="inline-flex h-6 w-6 items-center justify-center border-none bg-transparent text-[#3d3439] transition-colors duration-[180ms] ease-in-out hover:text-[#762a5c]"
            aria-label={`More info about ${course.name}`}
            onClick={onOpenPreview}
          >
            <Info className="h-4 w-4" />
          </button>
          <button
            type="button"
            className={cn(
              "inline-flex h-6 w-6 items-center justify-center border-none bg-transparent text-[#3d3439] transition-colors duration-[180ms] ease-in-out hover:text-[#762a5c]",
              isSaved && "text-[#762a5c]"
            )}
            aria-label={isSaved ? "Remove from saved" : "Save tutorial"}
            onClick={onToggleSaved}
          >
            <Heart className={cn("h-4 w-4", isSaved && "fill-current")} />
          </button>
        </div>
      </div>

      <button
        type="button"
        className="relative mt-4 w-full overflow-hidden rounded-[1rem] bg-[#ece7ea] [aspect-ratio:16/9]"
        onClick={onOpenPreview}
      >
        {course.thumbnail_links_course ? (
          <img
            src={course.thumbnail_links_course}
            alt={course.name}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="h-full w-full bg-gradient-to-br from-[#b3839f] to-[#6d2d58]" />
        )}
        <div className="absolute left-1/2 top-1/2 inline-flex h-20 w-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/35 text-white backdrop-blur-[8px]">
          <Play className="h-6 w-6" />
        </div>
      </button>
      {duration ? (
        <div className="mt-2 text-[0.85rem] font-bold text-black opacity-45">{duration}</div>
      ) : null}

      <div className="mt-4 flex flex-1 flex-col gap-[0.9rem]">
        <h3 className="m-0 font-display text-[2rem] leading-none text-[#341928] max-[767px]:text-[1.7rem]">{course.name}</h3>
        <p className="m-0 line-clamp-3 text-[1rem] leading-[1.65] text-[#5a5658] sm:text-[1.2rem]">
          {course.short_description ?? course.long_description ?? "Step into a polished, practice-ready routine."}
        </p>

        <div className="flex flex-wrap gap-[0.35rem]">
          {(course.category ?? []).map((tag) => (
            <span key={tag.name} className="inline-flex items-center rounded-full border border-[#aaa] bg-transparent px-[0.62rem] py-[0.32rem] text-[0.9rem] font-normal text-[#aaa]">
              {tag.name}
            </span>
          ))}
        </div>

        <div className="mt-auto flex flex-col gap-4">
          <div className="hidden">
            {course.is_free ? "Free" : `$${course.price}`}
          </div>
          {!isLoggedIn && !isOwned ? (
            <Link
              to={loginHref}
              className="inline-flex min-h-[51px] w-full items-center justify-center gap-2 rounded-full border border-[#762a5c] bg-[#762a5c] px-8 py-4 font-[Inter,sans-serif] text-[14px] font-medium leading-none text-white no-underline transition-colors hover:bg-[#5f2349]"
            >
              {primaryLabel}
            </Link>
          ) : isOwned ? (
            <button
              type="button"
              className="inline-flex min-h-[51px] w-full items-center justify-center gap-2 rounded-full border border-[#cef5ca] bg-[#cef5ca] px-8 py-4 font-[Inter,sans-serif] text-[14px] font-medium leading-none text-[#114e0b] opacity-70 cursor-default"
              disabled
            >
              {primaryLabel}
            </button>
          ) : course.is_free ? (
            <button
              type="button"
              className="inline-flex min-h-[51px] w-full items-center justify-center gap-2 rounded-full border border-[#762a5c] bg-white px-8 py-4 font-[Inter,sans-serif] text-[14px] font-medium leading-none text-[#762a5c] transition-colors hover:bg-[#faf1f6] disabled:opacity-50"
              disabled={addingFree}
              onClick={() => onAddFree(course.id)}
            >
              {primaryLabel}
            </button>
          ) : (
            <button
              type="button"
              className={cn(
                "inline-flex min-h-[51px] w-full items-center justify-center gap-2 rounded-full border border-[#762a5c] bg-[#762a5c] px-8 py-4 font-[Inter,sans-serif] text-[14px] font-medium leading-none text-white transition-colors hover:bg-[#5f2349]",
                inCart && "bg-white text-[#762a5c] hover:bg-[#f3edf1]"
              )}
              onClick={() => onAddToCart(course)}
            >
              <ShoppingCart className="h-4 w-4" />
              {primaryLabel}
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

function getPrimaryLabel(
  course: CourseWithDetails,
  isLoggedIn: boolean,
  isOwned: boolean,
  inCart: boolean,
  addingFree: boolean,
) {
  if (isOwned) return "Purchased";
  if (!isLoggedIn) return "Login to Purchase";
  if (course.is_free) return addingFree ? "Adding..." : "Add to Dashboard";
  if (inCart) return "Added to Cart";
  return "Add to Cart";
}
